/**
 * React hook layer for list-restore: decides whether to restore on this visit,
 * freezes the snapshot for the mount, and writes updates back to the store.
 */

import type { NavigationType } from "react-router";

import { useCallback, useLayoutEffect, useState } from "react";
import { useNavigationType } from "react-router";

import type { ListRestoreSnapshot } from "../utils/list-restore-store";

import {
	clearListRestorePending,
	clearListRestoreSnapshot,
	getListRestoreSnapshot,
	hasListRestorePending,
	hasListRestoreSnapshot,
	pruneExpiredListRestorePending,
	saveListRestoreSnapshot,
} from "../utils/list-restore-store";

/**
 * `"POP" | "PUSH" | "REPLACE"`, derived from react-router's `NavigationType`
 * so the values stay in sync with the router.
 *
 * Expanded to a string union rather than used as the enum directly: string
 * enums are nominal, so callers (and tests) could not pass a plain `"POP"`.
 */
type ListRestoreNavigationType = `${NavigationType}`;

type ListRestoreVisit = {
	listKey: string;
	/**
	 * The navigation type that produced `shouldRestore`, kept alongside it so
	 * the "discard on a fresh PUSH" cleanup cannot read a different value than
	 * the decision it is cleaning up after.
	 */
	navigationType: ListRestoreNavigationType;
	shouldRestore: boolean;
	snapshot: ListRestoreSnapshot | undefined;
};

/** One frozen read of store state for this mount / listKey. */
export const createListRestoreVisit = (
	listKey: string,
	navigationType: ListRestoreNavigationType,
): ListRestoreVisit => {
	pruneExpiredListRestorePending(listKey);
	const shouldRestore = shouldRestoreListVisit(navigationType, listKey);
	const raw = shouldRestore ? getListRestoreSnapshot(listKey) : undefined;
	return {
		listKey,
		navigationType,
		shouldRestore,
		snapshot: raw ? structuredClone(raw) : undefined,
	};
};

/**
 * Restore when a snapshot exists unless this is a fresh PUSH (sidebar /
 * bookmark) with no pending row click. A pending click restores even on PUSH
 * so in-app Back that cannot pop still returns to the same page.
 *
 * Filter changes while the list is mounted reset page via
 * `useResetTablePageOnChange`; restore is not gated on a filter fingerprint.
 */
export const shouldRestoreListVisit = (
	navigationType: ListRestoreNavigationType,
	listKey: string,
	pending = hasListRestorePending(listKey),
): boolean => {
	if (!hasListRestoreSnapshot(listKey)) {
		return false;
	}
	if (pending) {
		return true;
	}
	return navigationType !== "PUSH";
};

/**
 * Restores list position when returning to a list, not on a fresh visit.
 *
 * - PUSH without a pending row click (sidebar, bookmark): discard the snapshot.
 * - PUSH with a pending row click, POP, and REPLACE: restore the snapshot.
 *
 * Snapshot and shouldRestore are captured once per listKey visit so render-
 * phase persist writes do not shift restore anchor deps mid-restore.
 *
 * In-memory store dies on full reload.
 */
export const useListRestore = (
	listKey: string,
): {
	listKey: string;
	save: (snapshot: ListRestoreSnapshot) => void;
	shouldRestore: boolean;
	snapshot: ListRestoreSnapshot | undefined;
} => {
	const navigationType = useNavigationType();
	const [visitListKey, setVisitListKey] = useState(listKey);
	const [visit, setVisit] = useState(() =>
		createListRestoreVisit(listKey, navigationType),
	);

	// A new listKey is a new visit, so re-read the *live* navigation type.
	// Reusing the first mount's type would judge a fresh click into location B
	// by how the user arrived at location A, and restore a stale position.
	if (listKey !== visitListKey) {
		setVisitListKey(listKey);
		setVisit(createListRestoreVisit(listKey, navigationType));
	}

	const { shouldRestore, snapshot } = visit;

	useLayoutEffect(() => {
		if (visit.shouldRestore) {
			clearListRestorePending(visit.listKey);
			return;
		}
		// visit.navigationType, not the live one: `useNavigationType` is
		// app-wide, so an unrelated navigation elsewhere must not discard a
		// still-mounted list's snapshot.
		if (visit.navigationType === "PUSH") {
			clearListRestoreSnapshot(visit.listKey);
		}
	}, [visit]);

	const save = useCallback(
		(next: ListRestoreSnapshot) => {
			saveListRestoreSnapshot(listKey, next);
		},
		[listKey],
	);

	return { listKey, save, shouldRestore, snapshot };
};
